import { checkAccessRights, checkModuleAccess } from '@/modules'
import { USER_RIGHTS, SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import CustomerService from '@/modules/customer/customerService'
import SupplierService from '@/modules/supplier/supplierService'
import { UserService } from '@/modules/user/userService'
import { MultiSelectTypes } from '@/components/multi-select/constants'
import ClientService from '@/modules/client/clientService'

interface GetDataResponse {
  users?: any[],
  customers?: any[],
  suppliers?: any[],
  clients?: any[]
}

interface GetDataOptions {
  activeType: MultiSelectTypes,
  offset: number,
  limit: number,
  searchTerm?: string,
}

const MultiSelectService = {
  getData: async(options: GetDataOptions, userInfo?, isTaxConsultant = false, tenantCollaborationId?): Promise<GetDataResponse> => {
    try {
      const {
        activeType = MultiSelectTypes.All,
        offset = 0,
        limit = 30,
        searchTerm = '',
      } = options
      const hasCustomerModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.CUSTOMERS)
      const hasSupplierModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.SUPPLIERS)
      const hasClientModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.CLIENTS)
      const hasUserModuleAccess = checkAccessRights(USER_RIGHTS.MEMBERS.READ, userInfo)

      const response: GetDataResponse = {
        customers: [],
        suppliers: [],
        users: [],
        clients: [],
      }

      if ([MultiSelectTypes.All, MultiSelectTypes.Customer].includes(activeType) && hasCustomerModuleAccess) {
        const { data: customers } = await CustomerService.find(searchTerm, offset, limit, true)
        response.customers = customers.reduce((result, customer) => {
          customer.multiSelectType = MultiSelectTypes.Customer
          const contactPersons = customer.contactPersons.map((contactPerson) => {
            contactPerson.multiSelectType = MultiSelectTypes.CustomerContact
            contactPerson.number = customer.number
            return contactPerson
          })
          return [...result, customer, ...contactPersons]
        }, [])
      }

      if ([MultiSelectTypes.All, MultiSelectTypes.Supplier].includes(activeType) && hasSupplierModuleAccess) {
        const { data: suppliers } = await SupplierService.find(searchTerm, offset, limit, true)
        response.suppliers = suppliers.reduce((result, supplier) => {
          supplier.multiSelectType = MultiSelectTypes.Supplier
          const contactPersons = supplier.contactPersons.map((contactPerson) => {
            contactPerson.multiSelectType = MultiSelectTypes.SupplierContact
            contactPerson.number = supplier.number
            return contactPerson
          })
          return [...result, supplier, ...contactPersons]
        }, [])
      }

      if ([MultiSelectTypes.All, MultiSelectTypes.User].includes(activeType) && hasUserModuleAccess) {
        const { data: users } = await UserService.find(searchTerm, offset, limit, true)
        response.users = users.map((user) => {
          user.multiSelectType = MultiSelectTypes.User
          return user
        })
      }

      if ([MultiSelectTypes.All, MultiSelectTypes.Client].includes(activeType) && hasClientModuleAccess && isTaxConsultant) {
        const { data: clients } = await ClientService.find(searchTerm, offset, limit, tenantCollaborationId)
        response.clients = clients.map((client) => {
          client.multiSelectType = MultiSelectTypes.Client
          return client
        })
      }

      return response
    } catch (error) {
      throw error
    }
  },
}

export default MultiSelectService

export { MultiSelectService }
